import { useEffect, useMemo, useRef, useState } from 'react';
import { CampingSpot, GpxRoute, SearchResult } from '../types';
import { searchPlace } from '../services/geocodingService';
import { logFeatureUsage } from '../services/firebaseService';

const DEFAULT_ROUTE_FALLBACK_CENTER = { latitude: 59.9139, longitude: 10.7522 };
const NORTH_ROUTE_FALLBACK_CENTER = { latitude: 69.6492, longitude: 18.9553 };
const CENTRAL_ROUTE_FALLBACK_CENTER = { latitude: 63.4305, longitude: 10.3951 };

const getFallbackRouteCenter = (region: string | null | undefined) => {
  const reg = String(region || '').toLowerCase();
  if (reg.includes('north') || reg.includes('nord')) return NORTH_ROUTE_FALLBACK_CENTER;
  if (reg.includes('central') || reg.includes('midt')) return CENTRAL_ROUTE_FALLBACK_CENTER;
  return DEFAULT_ROUTE_FALLBACK_CENTER;
};


export const useAppSearch = (searchQuery: string, spots: CampingSpot[], routes: GpxRoute[], allowedCountries: string[] = ['no']) => {
  const [localResults, setLocalResults] = useState<SearchResult[]>([]);
  const [photonResults, setPhotonResults] = useState<SearchResult[]>([]);
  const [isSearchingPhoton, setIsSearchingPhoton] = useState(false);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const q = searchQuery.trim().toLowerCase();
    if (q.length < 2) {
      setLocalResults([]);
      return;
    }

    const matchedSpots = [...spots]
      .filter((spot) => spot.isVerified && !!spot?.name)
      .map((spot) => {
        const name = (spot.name || '').trim();
        const normalized = name.toLowerCase();
        const exact = normalized === q ? 0 : 1;
        const starts = normalized.startsWith(q) ? 0 : 1;
        const includes = normalized.includes(q) ? 0 : 1;
        return { item: spot, name, type: 'spot' as const, id: spot.id, score: exact * 100 + starts * 10 + includes };
      })
      .filter((entry) => entry.score < 111);

    const matchedRoutes = [...routes]
      .filter((route) => !!route?.name)
      .map((route) => {
        const name = (route.name || '').trim();
        const normalized = name.toLowerCase();
        const exact = normalized === q ? 0 : 1;
        const starts = normalized.startsWith(q) ? 0 : 1;
        const includes = normalized.includes(q) ? 0 : 1;
        return { item: route, name, type: 'route' as const, id: route.id, score: exact * 100 + starts * 10 + includes };
      })
      .filter((entry) => entry.score < 111);

    const nextLocalResults: SearchResult[] = [...matchedSpots, ...matchedRoutes]
      .sort((a, b) => a.score - b.score || a.name.length - b.name.length)
      .slice(0, 8)
      .map((entry) => {
        let lat = 0;
        let lon = 0;

        if (entry.type === 'spot') {
          lat = entry.item.location.latitude;
          lon = entry.item.location.longitude;
        } else if (entry.item.center) {
          lat = entry.item.center.latitude;
          lon = entry.item.center.longitude;
        } else {
          const fallback = getFallbackRouteCenter(entry.item.region);
          lat = fallback.latitude;
          lon = fallback.longitude;
        }

        return {
          display_name: entry.name,
          lat: String(lat),
          lon: String(lon),
          type: entry.type === 'spot' ? 'spot' : 'road',
          class: `${entry.type}:${entry.id}`,
          boundingbox: entry.type === 'spot'
            ? [String(lat - 0.01), String(lat + 0.01), String(lon - 0.01), String(lon + 0.01)]
            : [String(lat - 0.05), String(lat + 0.05), String(lon - 0.05), String(lon + 0.05)]
        };
      });

    setLocalResults(nextLocalResults);
  }, [searchQuery, spots, routes]);

  useEffect(() => {
    const q = searchQuery.trim().toLowerCase();
    if (q.length < 3) {
      setPhotonResults([]);
      setIsSearchingPhoton(false);
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
      return;
    }

    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    setIsSearchingPhoton(true);
    searchTimeout.current = setTimeout(async () => {
      try {
        const remoteResults = await searchPlace(searchQuery, allowedCountries);
        logFeatureUsage('search');
        setPhotonResults(remoteResults);
      } catch (err) {
        console.error('Search fetch error:', err);
      } finally {
        setIsSearchingPhoton(false);
      }
    }, 300);

    return () => {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
    };
  }, [searchQuery, allowedCountries]);

  const mergedSearchResults = useMemo(() => {
    const seen = new Set(
      localResults.map((result) => `${result.display_name.toLowerCase()}|${parseFloat(result.lat).toFixed(3)}|${parseFloat(result.lon).toFixed(3)}`)
    );
    const combined = [...localResults];
    photonResults.forEach((result) => {
      const key = `${result.display_name.toLowerCase()}|${parseFloat(result.lat).toFixed(3)}|${parseFloat(result.lon).toFixed(3)}`;
      if (!seen.has(key)) {
        combined.push(result);
        seen.add(key);
      }
    });
    return combined;
  }, [localResults, photonResults]);

  return {
    isSearchingPhoton,
    mergedSearchResults,
  };
};
